import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Wrapper } from 'components/theme/Wrapper';
import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import { PageTitle } from 'components/atoms/PageTitle';
import {
  ListingKind,
  ANALYTICS_EVENT,
  ListingAddOnLimitType,
  AVAILABLE_BRAND_FEATURES,
  CreateBookingInquiryEndpoint,
  DateType,
  InquiryItemType,
  ANALYTICS_PROPERTY,
  IntegrationName,
} from '@kouto/types';
import { fetchBrandTerms } from 'actions/brand';
import { BookingMode } from 'types/payment';
import { CartItemState } from 'types/cart';
import { P as Paragraph } from 'components/theme/Typography/Typography';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import { NavigationHeader } from 'components/NavigationHeader';
import { removeExperienceFromCart } from 'actions/cart';
import useSearchQueryParams from 'hooks/use-search-params';
import useCustomQuestions from 'hooks/useCustomQuestions';
import useCustomQuestionsApi from 'api/experience/useCustomQuestions/useCustomQuestions';
import useResourceOptions from 'api/experience/useResourceOptions';
import { ResourceAttributePicker } from 'components/ResourceAttributePicker';
import {
  deriveResourceSlots,
  isResourceSelectionComplete,
  ParticipantSelections,
  sanitizeResourceSelections,
} from 'utils/resourceSelections/resourceSelections';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import { useCartItemId } from 'features/ShoppingCart/hooks';
import { ParticipantField } from 'features/ShoppingCart/components/ParticipantField';
import {
  IParticipantConfig,
  ISelectOptions,
} from 'features/ShoppingCart/types';
import { useCart } from 'hooks/useCart';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button/Button';
import { decodeHTML } from 'utils';
import { splitFullName } from 'utils/string';
import { useToastContext } from 'ToastProvider';
import { getExperienceSettings } from 'actions/experience';
import * as Styled from 'features/ShoppingCart/utils/styles';
import useFetchExperience from 'selectors/experience';
import { QUESTION_SCOPES } from 'types/custom-questions';
import CustomQuestions from 'components/CustomQuestion/CustomQuestions';
import { STATUS_PENDING } from 'types/app';
import { usePageViewEvent } from 'hooks/usePageViewEvent';
import { useExperienceAnalyticsData } from 'features/analytics/hooks/useExperienceAnalyticsData';
import ListingCartItemSummary from 'features/ShoppingCart/components/ListingCartItemSummary';
import { useEmbedConfig } from 'features/EmbedConfig';
import useOneCartItemListing from 'hooks/useOneCartItemListing';
import { Session } from 'types/listings';
import { BASE_URL } from 'consts';
import createBookingRequest from 'api/requestToBook/createBookingRequest';
import {
  IncludedTax,
  NonIncludedTax,
  Tier,
  Listing as CartListing,
  computeCartItemWithCompoundTaxes,
  MinimalCartItem,
} from '@kouto/cart-prices';
import {
  adaptListingV3ForCart,
  adaptCartItemForCart,
} from 'utils/data/taxes/taxes';
import { analyticsManager } from 'features/analytics/services/AnalyticsManager';
import { summarizeFormErrors } from 'features/analytics/utils/formErrors';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import { isFeatureActive } from 'utils/featureFlag';
import RequestToBookParticipantForm from './RequestToBookParticipantForm';
import HyattWaiver from './HyattWaiver';

type CartItemTotalDetails = {
  isPrivateBooking: boolean;
  selectedPriceTiers: Tier[];
  includedTaxes: IncludedTax[];
  nonIncludedTaxes: NonIncludedTax[];
  subTotal: number;
  total: number;
};

const formatFullName = (name: string | ISelectOptions) => {
  if (typeof name === 'string') {
    return name.trim();
  }
  return {
    label: name?.label.trim(),
    value: name?.value,
  };
};

const formatDateRange = (sessions: Session[]) => {
  const dates = sessions.map((session) => new Date(session.startDateTime));
  const sortedDates = dates.sort((a, b) => a.getTime() - b.getTime());
  const [start, end] = [sortedDates[0], sortedDates[sortedDates.length - 1]];
  const sameMonth = start.getMonth() === end.getMonth();
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  const startStr = start.toLocaleDateString('en-US', options);
  const endStr = sameMonth
    ? end.getDate()
    : end.toLocaleDateString('en-US', options);

  return `${startStr} – ${endStr}`;
};

const ParticipantsForm = () => {
  const { maxFieldCapacity, onEditCart } = useCart();
  const { currentExperienceDetail } = useExperienceAnalyticsData();
  const { purchaserParentQuestions } = useCustomQuestions();
  const cartItemId = useCartItemId();
  const dispatch = useDispatch();
  const brandId = useBrandId();
  const methods = useForm<IParticipantConfig>({ mode: 'onChange' });
  const { cartItem, listing, isLoading } = useOneCartItemListing(cartItemId);
  const cartCompatibleListing: CartListing | undefined = listing
    ? adaptListingV3ForCart(listing)
    : undefined;
  const history = useHistory();
  // Carry the current route's query params onto the redirect. Read from
  // react-router's location so it is populated in every router mode;
  // window.location.hash is empty outside hash mode.
  const querySuffix = useLocation().search || '';
  const { t: translateText } = useTranslation();
  const { searchParams } = useSearchQueryParams();
  const hasPurchaserQuestions = purchaserParentQuestions.length > 0;
  const skipParticipantNames =
    searchParams.skipParticipantsName === 'true' || hasPurchaserQuestions;
  const { settings } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );
  const isRequestToBookEnabled = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_REQUEST_BOOKINGS,
  );
  const hasRemoveAddToCart = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_HIDE_ADD_TO_CART_BUTTON,
  );
  const { showToast } = useToastContext();
  const { experience, fetchSettingsStatus } = useFetchExperience();
  const embedConfig = useEmbedConfig();
  const languageCode = useSupportedLanguage();
  // Gate submission until the experience's custom questions have loaded. The
  // child CustomQuestions/ParticipantField components register their required
  // validation only once this fetch resolves; without the guard a fast submit
  // (e.g. a pre-filled name + slow network) passes validation with no questions
  // registered and books with empty custom-question responses. Same SWR args as
  // the children so the request is deduped.
  const { isLoading: isLoadingCustomQuestions } = useCustomQuestionsApi({
    brandId,
    // `experience` is a dummy placeholder (loading: true) with a throwaway id
    // until the real fetch resolves; skip until then to avoid a 404 on that id.
    experienceId: experience?.loading ? undefined : experience?.id,
    language: languageCode,
  });
  const customQuestionsNotReady = isLoadingCustomQuestions;
  const {
    fields,
    append,
    remove: removeField,
  } = useFieldArray({
    control: methods.control,
    name: 'participants',
  });
  // Radix portals default to document.body, outside the embed's styled root.
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const { data: resourceOptions, isLoading: isLoadingResourceOptions } =
    useResourceOptions({
      experienceId: cartItem?.experienceId,
      startDateTime: cartItem?.sessionDateTime,
      sessionDuration: cartItem?.sessionDuration,
      // A group's picks are made on its page, where the open resources are known.
      skip: isRequestToBookEnabled || !!cartItem?.groupId,
    });
  const watchedParticipants = methods.watch('participants');
  const resourceParticipants = fields.map((field, index) => ({
    priceTierName: field.priceTierName,
    resourceSelections: watchedParticipants?.[index]?.resourceSelections,
  }));
  const resourceSlots = deriveResourceSlots(
    resourceParticipants,
    resourceOptions ?? [],
  );
  // Re-sanitized on every options refresh so a value that disappeared cannot stay picked.
  const resourceSelections: ParticipantSelections = resourceOptions
    ? sanitizeResourceSelections(resourceParticipants, resourceOptions)
    : resourceParticipants.map(
        (participant) => participant.resourceSelections ?? [],
      );
  const isSubmitBlocked =
    customQuestionsNotReady ||
    isLoadingResourceOptions ||
    !isResourceSelectionComplete(resourceSlots, resourceSelections);
  const showResourcePicksOnly =
    skipParticipantNames && !hasPurchaserQuestions && resourceSlots.length > 0;

  const defaultHeadingKey = cartItemId
    ? 'editParticipantsDetails'
    : 'participantDetails';
  const headingKey = showResourcePicksOnly
    ? 'resourceSelectionTitle'
    : defaultHeadingKey;

  const handleResourceSelectionsChange = (next: ParticipantSelections) => {
    next.forEach((participantSelections, index) => {
      methods.setValue(
        `participants.${index}.resourceSelections`,
        participantSelections.length ? participantSelections : undefined,
        { shouldDirty: true },
      );
    });
  };

  const renderResourcePicker = (participantIndex?: number) =>
    resourceSlots.length > 0 && (
      <ResourceAttributePicker
        onChange={handleResourceSelectionsChange}
        options={resourceOptions ?? []}
        participantIndex={participantIndex}
        participants={resourceParticipants}
        portalContainer={portalContainer}
        value={resourceSelections}
      />
    );

  const requestBody = listing
    ? {
        listings: [
          {
            id: listing.id,
            brandId: listing.brandId,
            productLine: listing.productLine,
          },
        ],
      }
    : null;

  const { data: eligibleProgram } = useGetEligibleProgram(
    listing?.brandId,
    requestBody,
  );

  const showHyattWaiver =
    listing?.id && eligibleProgram
      ? eligibleProgram?.[listing?.id]?.earnEligible
      : false;

  const populateFormFields = () => {
    if (!cartItemId || !cartItem || fields.length >= 1) return;
    append(
      cartItem.participants.map((p) => ({
        fullName: p.fullName || '',
        terms: p.terms,
        priceTierName: p.priceTierName,
        price: p.price,
        resourceSelections: p.resourceSelections,
      })),
      {
        focusIndex: 0,
      },
    );
    methods.setValue(
      'additionalCustomQuestionResponses',
      cartItem.additionalCustomQuestionResponses,
    );
  };

  const currentCartItem = useMemo(() => {
    if (cartItem?.cartItemId) {
      return {
        ...cartItem,
        cartItemId: cartItem.cartItemId,
        participants:
          skipParticipantNames || fields.length === 0
            ? cartItem?.participants ?? []
            : fields.map((p) => ({
                firstName: '',
                lastName: '',
                fullName: '',
                priceTierName: p.priceTierName,
                price: p.price,
                terms: p.terms,
                customQuestionResponses: p?.customQuestionResponses ?? {},
              })),
        addOns: cartItem?.addOns ?? [],
      };
    }
    return null;
  }, [cartItem, fields, skipParticipantNames]);

  const { includedTaxes, nonIncludedTaxes, subTotal, total } =
    useMemo<CartItemTotalDetails>(() => {
      if (!currentCartItem || !listing || !currentCartItem.cartItemId) {
        return {
          isPrivateBooking: false,
          selectedPriceTiers: [],
          includedTaxes: [],
          nonIncludedTaxes: [],
          subTotal: 0,
          total: 0,
        };
      }

      const adaptedCartItem = adaptCartItemForCart(currentCartItem);

      return computeCartItemWithCompoundTaxes(
        adaptedCartItem as unknown as MinimalCartItem,
        cartCompatibleListing as CartListing,
        undefined,
        undefined,
        undefined,
        isRequestToBookEnabled,
      );
    }, [currentCartItem, listing, isRequestToBookEnabled]);

  useEffect(() => {
    if (!experience.loading && brandId) {
      dispatch(fetchBrandTerms(brandId));
      populateFormFields();
      dispatch(getExperienceSettings(brandId, experience?.id));
    }
  }, [experience.id, experience.loading, brandId]);

  const resourceGroupTitle =
    listing?.resourceGroups?.find(
      (resourceGroup) => resourceGroup.id === experience.resourceGroupId,
    )?.title || '';

  usePageViewEvent({
    eventName: ANALYTICS_EVENT.VIEW_PARTICIPANT_INFO,
    isNotReady: !currentExperienceDetail?.id,
    payload: {
      [ANALYTICS_PROPERTY.PageName]: `${translateText(
        'details',
      )} | ${resourceGroupTitle} | ${settings?.name}`,
      [ANALYTICS_PROPERTY.PageType]: 'Participant info page',
      [ANALYTICS_PROPERTY.ExperienceTitle]:
        experience?.title ??
        listing?.title ??
        `Title not found for listing ID: ${listing?.id}. Please contact support.`,
    },
  });

  const cartItemContainsMultiplePriceTiers = useMemo(
    () =>
      new Set(
        cartItem?.participants?.map((participant) => participant.priceTierName),
      ).size > 1,
    [cartItem],
  );

  const canAddParticipants = useMemo(() => {
    const eligibleSize = cartItemId
      ? experience?.partySize - fields.length
      : experience?.partySize -
        fields.length -
        (cartItem?.participants?.length ?? 0);

    const isSlotSizeMet =
      searchParams.mode === BookingMode.SHARED &&
      (eligibleSize === 0 || fields.length === maxFieldCapacity);

    const selectedPriceTier = experience?.priceTiers?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (pt: any) => pt.name === cartItem?.participants[0]?.priceTierName,
    );

    return (
      !isSlotSizeMet &&
      (Number.isNaN(Number(experience?.maxParticipantsCount)) ||
        fields.length < experience?.maxParticipantsCount) &&
      !cartItem?.waitlistEntryId &&
      !cartItemContainsMultiplePriceTiers &&
      (!selectedPriceTier || selectedPriceTier?.maxQuantity > fields.length) &&
      purchaserParentQuestions.length === 0
    );
  }, [
    cartItemId,
    cartItem,
    experience,
    maxFieldCapacity,
    fields,
    purchaserParentQuestions,
    searchParams,
    cartItemContainsMultiplePriceTiers,
  ]);

  if (cartItemId && !cartItem && !isLoading) {
    return (
      <Wrapper>
        <NavigationHeader />
      </Wrapper>
    );
  }

  const participantsInfoMapper = (
    participants: IParticipantConfig['participants'],
  ) => {
    return participants.map((p) => {
      const name = isObject(p.fullName) ? p.fullName.label : p.fullName;
      const { firstName, lastName } = splitFullName(name);
      return {
        ...p,
        fullName: name,
        firstName,
        lastName,
      };
    });
  };

  const updateCartItemAndRedirect = async (
    data: IParticipantConfig,
    route: string,
    overrideSearch?: string,
    isRequestToBook = false,
  ) => {
    if (cartItem) {
      let search = overrideSearch;

      if (!search) {
        const purchaserParams = new URLSearchParams();
        if (searchParams.firstName) {
          purchaserParams.append('firstName', searchParams.firstName);
        }
        if (searchParams.lastName) {
          purchaserParams.append('lastName', searchParams.lastName);
        }
        if (searchParams.emailAddress) {
          purchaserParams.append('emailAddress', searchParams.emailAddress);
        }
        if (searchParams.phoneNumber) {
          purchaserParams.append('phoneNumber', searchParams.phoneNumber);
        }
        search = purchaserParams.toString();
      }

      // Fire analytics event only on successful validation and submission instead of on every click
      analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_ADD_TO_CART_BUTTON);

      onEditCart(
        {
          ...cartItem,
          ...data,
          state: isRequestToBook ? CartItemState.PENDING : CartItemState.READY,
          participants: participantsInfoMapper(data.participants),
        },
        route,
        search,
        !!isRequestToBook,
      );
    }
  };

  const onDeleteFromCart = () => {
    if (cartItemId) {
      dispatch(
        removeExperienceFromCart({
          cartItemId,
          persistMeta: {
            brandId,
          },
        }),
      );
      history.push('/e');
      showToast({
        title: 'Success',
        message: 'Item removed from cart',
      });
    }
  };

  const onAddParticipants = () => {
    append({
      fullName: embedConfig?.participantName?.[fields.length] || '',
      terms: false,
      priceTierName:
        cartItem?.participants[0]?.priceTierName ??
        get(experience, 'priceTiers[0].name'),
      price:
        cartItem?.participants[0]?.price ??
        get(experience, 'priceTiers[0].price'),
    });
  };

  const onDeleteParticipant = (participantIndex: number) => {
    removeField(participantIndex);
    const newParticipantsCount = fields.length - 1;

    if (cartItem && cartItem.addOns) {
      cartItem.addOns = cartItem.addOns.map((addOn) => {
        const litingAddOn = listing?.addOns?.find(
          (listingAddOn) => listingAddOn?.addOnId === addOn.addOnId,
        );

        if (litingAddOn && litingAddOn.limit) {
          const listingLimit =
            litingAddOn.limitType === ListingAddOnLimitType.PER_BOOKING
              ? litingAddOn.limit
              : litingAddOn.limit * newParticipantsCount;

          return {
            ...addOn,
            selectedNumber: Math.min(addOn.selectedNumber, listingLimit),
          };
        }

        return addOn;
      });

      onEditCart(cartItem);
    }
  };

  const formatCustomQuestionResponses = (dataCopy: IParticipantConfig) => {
    return Object.entries(dataCopy.additionalCustomQuestionResponses)
      .filter(([, answer]) => answer != null)
      .map(([questionId, answer]) => ({
        questionId,
        answer,
      }));
  };

  const formatIncludedTaxes = includedTaxes.map((tax: IncludedTax) => ({
    label: tax.name,
    type: InquiryItemType.TAX,
    amount: tax.percentage,
  }));

  const formatNonIncludedTaxes = nonIncludedTaxes.map(
    (tax: NonIncludedTax) => ({
      label: tax.name,
      type: InquiryItemType.TAX,
      amount: tax.amountFee,
    }),
  );

  const handleCreateBookingRequest = async (
    dataCopy: IParticipantConfig,
    redirectPath: string,
  ) => {
    const urlPath = `${redirectPath}${querySuffix}`;
    try {
      const endpoint = CreateBookingInquiryEndpoint.url({
        brandId,
        experienceId: experience.id,
      });
      const url = `${BASE_URL}/${endpoint}`;

      if (dataCopy.participant) {
        const requestBody = {
          customer: {
            firstName: dataCopy.participant.firstName,
            lastName: dataCopy.participant.lastName,
            emailAddress: dataCopy.participant.emailAddress,
            phoneNumber: dataCopy.participant.phoneNumber ?? null,
          },
          purchaser: {
            firstName: dataCopy.participant?.firstName,
            lastName: dataCopy.participant?.lastName,
            emailAddress: dataCopy.participant?.emailAddress,
            phoneNumber: dataCopy.participant?.phoneNumber,
            customQuestionResponses: formatCustomQuestionResponses(dataCopy),
          },
          inquiryItems: [
            {
              label: 'subtotal',
              type: InquiryItemType.ITEM,
              amount: subTotal,
            },
            {
              label: 'total',
              type: InquiryItemType.ITEM,
              amount: total,
            },
            ...formatIncludedTaxes,
            ...formatNonIncludedTaxes,
          ],
          startDate:
            cartItem?.sessions?.[0]?.startDateTime.split('T')[0] ?? null,
          endDate:
            cartItem?.sessions?.[
              cartItem?.sessions.length - 1
            ]?.startDateTime.split('T')[0] ?? null,
          dateType: DateType.HOTEL,
        };
        await createBookingRequest(
          url,
          {
            brandId,
          },
          requestBody,
        );
      }
      updateCartItemAndRedirect(dataCopy, urlPath, undefined, true);
    } catch (error) {
      showToast({
        title: 'Failed to send booking request',
        type: 'error',
      });
      console.error('error', error);
    }
  };

  const handleSubmit = (redirectPath: string, retry = false) => {
    return methods.handleSubmit(
      (data) => {
        const dataCopy = {
          ...data,
          participants: data.participants.map((participant, index) => ({
            ...participant,
            resourceSelections: resourceSelections[index]?.length
              ? resourceSelections[index]
              : undefined,
            fullName: skipParticipantNames
              ? 'Resource Purchaser'
              : formatFullName(participant.fullName),
            firstName: skipParticipantNames ? 'Resource' : undefined,
            lastName: skipParticipantNames ? 'Purchaser' : undefined,
          })),
        };

        if (cartItem?.listingId) {
          dataCopy.listingId = cartItem.listingId;
        }
        if (cartItem?.groupId) {
          dataCopy.groupId = cartItem.groupId;
          const resourceGroup = listing?.resourceGroups?.find(
            (rg) => rg.id === cartItem.groupId,
          );
          if (resourceGroup) {
            dataCopy.groupTitle =
              resourceGroup.title ?? 'Untitled resource group';
          }
        }

        if (
          redirectPath === '/checkout' ||
          redirectPath === '/request-to-book/confirmation'
        ) {
          // Fire analytics event only on successful validation and submission instead of on every click
          analyticsManager.sendEvent(
            ANALYTICS_EVENT.CLICK_PROCEED_TO_CHECKOUT_BUTTON,
          );

          if (redirectPath === '/request-to-book/confirmation') {
            handleCreateBookingRequest(dataCopy, redirectPath);
            return null;
          }

          if (redirectPath !== '/checkout') {
            if (listing && listing.kind !== ListingKind.EXPERIENCE) {
              return updateCartItemAndRedirect(
                dataCopy,
                `/${
                  listing.kind === ListingKind.RESOURCE ? 'collection' : 'event'
                }/${listing.id}`,
                `date=${searchParams.date}`,
              );
            }
          }
        }
        return updateCartItemAndRedirect(dataCopy, redirectPath);
      },
      (errors, ev) => {
        // Handle weird react-hook-form bug where participants error array is empty
        // https://github.com/react-hook-form/react-hook-form/issues/9902
        const { participants: participantsErrors } = errors;
        if (
          !retry &&
          Array.isArray(participantsErrors) &&
          !participantsErrors.find((err) => !!err)
        ) {
          methods.clearErrors();
          requestAnimationFrame(() => handleSubmit(redirectPath, true)(ev));
          return;
        }
        analyticsManager.sendEvent(
          ANALYTICS_EVENT.FORM_VALIDATION_ERROR,
          {
            [ANALYTICS_PROPERTY.ErrorCode]: 'participant_form',
            [ANALYTICS_PROPERTY.ErrorMessage]: summarizeFormErrors(errors),
          },
          { providers: [IntegrationName.ADOBE_ANALYTICS] },
        );
      },
    );
  };

  return (
    <Wrapper>
      <PageTitle subTitle="details" translatedTitle={resourceGroupTitle} />
      <NavigationHeader />
      {!!cartItem?.participants && cartItem?.participants.length > 0 && (
        <PageLayout>
          <div ref={setPortalContainer}>
            {experience?.loading || fetchSettingsStatus === STATUS_PENDING ? (
              <>
                <SkeletonLine style={{ height: 100 }} translucent />
                <SkeletonLine style={{ height: 300 }} translucent />
              </>
            ) : (
              <>
                {isRequestToBookEnabled ? (
                  <>
                    <Styled.RequestToBookTitle className="page-heading">
                      {listing?.title}
                    </Styled.RequestToBookTitle>
                    <Styled.RequestToBookDate className="page-heading-help-text">
                      {formatDateRange(cartItem?.sessions ?? [])}
                    </Styled.RequestToBookDate>
                  </>
                ) : (
                  <>
                    <Styled.HeadTitle as="h2" className="page-heading">
                      {translateText(headingKey)}
                    </Styled.HeadTitle>
                    <Paragraph className="page-heading-help-text">
                      {translateText(
                        showResourcePicksOnly
                          ? 'resourceSelectionDescription'
                          : 'enterParticipantInfoText',
                      )}
                    </Paragraph>
                  </>
                )}

                <FormProvider {...methods}>
                  <Styled.FormSection>
                    {customQuestionsNotReady ? (
                      <>
                        <SkeletonLine style={{ height: 60 }} translucent />
                        <SkeletonLine style={{ height: 200 }} translucent />
                      </>
                    ) : (
                      <>
                        {isRequestToBookEnabled ? (
                          <RequestToBookParticipantForm />
                        ) : (
                          <>
                            {skipParticipantNames
                              ? renderResourcePicker()
                              : fields.map((field, i) => {
                                  return (
                                    <ParticipantField
                                      idx={i}
                                      priceTierName={field.priceTierName}
                                      onRemove={() => onDeleteParticipant(i)}
                                      fieldsLength={fields.length}
                                      key={field.id}
                                      experienceId={experience.id}
                                    >
                                      {renderResourcePicker(i)}
                                    </ParticipantField>
                                  );
                                })}
                          </>
                        )}
                        {canAddParticipants && (
                          <AddParticipantContainer>
                            <PrimaryButton
                              className="cart__add-participant-button"
                              type="button"
                              onClick={onAddParticipants}
                            >
                              {decodeHTML(
                                translateText('addParticipant', {
                                  tierLabel:
                                    cartItem?.participants[0]?.priceTierName,
                                }),
                              )}
                            </PrimaryButton>
                          </AddParticipantContainer>
                        )}
                        <CustomQuestions
                          scope={QUESTION_SCOPES.PURCHASER}
                          formStatePath="additionalCustomQuestionResponses"
                          experienceId={experience.id}
                        />
                      </>
                    )}
                    {isRequestToBookEnabled ? (
                      <PrimaryButton
                        big
                        className="cart__form-button-full cart__proceed-to-checkout-button"
                        disabled={isSubmitBlocked}
                        onClick={handleSubmit('/request-to-book/confirmation')}
                      >
                        {translateText('submit')}
                      </PrimaryButton>
                    ) : (
                      <>
                        {cartItem?.state === CartItemState.READY ? (
                          <Styled.FooterButtons>
                            <PrimaryButton
                              big
                              className="cart__form-button-full cart__save-changes-button"
                              disabled={isSubmitBlocked}
                              onClick={handleSubmit('/checkout')}
                              role="button"
                            >
                              {translateText('saveChanges')}
                            </PrimaryButton>
                            <SecondaryButton
                              big
                              className="cart__form-button-clear cart__cancel-changes-button"
                              onClick={() => history.push('/checkout')}
                              role="button"
                            >
                              {translateText('cancelChanges')}
                            </SecondaryButton>
                            {showHyattWaiver && <HyattWaiver />}
                          </Styled.FooterButtons>
                        ) : (
                          <Styled.FooterButtons>
                            <PrimaryButton
                              big
                              className="cart__form-button-full cart__proceed-to-checkout-button"
                              disabled={
                                String(searchParams.latest).toLowerCase() ===
                                  'true' || isSubmitBlocked
                              }
                              onClick={handleSubmit('/checkout')}
                              role="button"
                            >
                              {translateText('proceedToCheckout')}
                            </PrimaryButton>
                            {!hasRemoveAddToCart && (
                              <SecondaryButton
                                big
                                className="cart__form-button-clear cart__add-to-cart-button"
                                disabled={
                                  String(searchParams.latest).toLowerCase() ===
                                    'true' || isSubmitBlocked
                                }
                                onClick={handleSubmit('/search')}
                                role="button"
                              >
                                {translateText('addToCart')}
                              </SecondaryButton>
                            )}
                            {showHyattWaiver && <HyattWaiver />}
                          </Styled.FooterButtons>
                        )}
                      </>
                    )}
                  </Styled.FormSection>
                </FormProvider>
              </>
            )}
          </div>
          <div>
            {cartItem?.state === CartItemState.READY && (
              <PrimaryButton
                big
                className="cart__form-button-full cart__remove-from-reservation-button"
                onClick={onDeleteFromCart}
                style={{ marginBottom: '20px' }}
              >
                {translateText('removeFromReservation')}
              </PrimaryButton>
            )}

            {cartItem && listing && (
              <ListingCartItemSummary
                isRequestToBookEnabled={isRequestToBookEnabled}
                cartItem={cartItem}
                listing={listing}
                overriddenParticipants={
                  skipParticipantNames && fields.length > 0
                    ? undefined
                    : fields.map((p) => ({
                        firstName: '',
                        lastName: '',
                        fullName: '',
                        priceTierName: p.priceTierName,
                        price: p.price,
                        terms: p.terms,
                        customQuestionResponses:
                          p?.customQuestionResponses ?? {},
                      }))
                }
              />
            )}
          </div>
        </PageLayout>
      )}
    </Wrapper>
  );
};

const AddParticipantContainer = styled.div`
  margin-bottom: 60px;
`;

const PageLayout = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 100%;
  gap: 36px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  & > div:first-child {
    flex-grow: 1;
    max-width: 720px;

    @media (max-width: 768px) {
      max-width: unset;
    }
  }
  & > div:last-child {
    min-width: 350px;

    @media (max-width: 768px) {
      min-width: unset;
    }
  }
`;

export default ParticipantsForm;
